// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a React Query mutation hook for updating organization
 * settings, with automatic cache invalidation on success.
 */
import {
  UpdateOrganizationParameters,
  updateOrganization,
} from '@/account/api/update-organization';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface UpdateOrganizationVariables {
  orgKey: string;
  parameters: UpdateOrganizationParameters;
}

/**
 * Hook for updating an organization.
 * Invalidates the current-user and organization caches on success.
 *
 * @returns Mutation object with mutate function and state
 */
export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgKey, parameters }: UpdateOrganizationVariables) =>
      updateOrganization(orgKey, parameters),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['current-user'] });
      await queryClient.invalidateQueries({
        queryKey: ['organization', variables.orgKey],
      });
    },
  });
}
